import { useNavigate } from 'react-router'
import PropTypes from 'prop-types'
import SDButton from '@/shared/components/SDComponents/SDButton/SDButton'
import { StyledTable, SortableHeadCell, StyledBodyCell } from 'baseui/table-grid'
import { withStyle, useStyletron } from 'baseui'
import { SIZE } from 'baseui/button'
import IconCarbonCheckmarkOutline from '@/shared/icons/IconCarbonCheckmarkOutline'
import IconCarbonPauseOutline from '@/shared/icons/IconCarbonPauseOutline'
import IconCarbonWarningAlt from '@/shared/icons/IconCarbonWarningAlt'
import { useTranslation } from 'react-i18next'
import ComparatorLinkState from './ComparatorLinkState'
import useOfferManagement from '../../OrganizationShow/Common/useOfferManagement'
import useDashboardTableStyles from '../../../DashboardPageV2/DashboardTable/useDashboardTableStyles'

const EnergyOffersTable = ({
  items,
  providerUuid,
  deleteRoute,
  duplicateRoute,
  onDeleteSuccess,
  onDuplicateSuccess,
}) => {
  const { t } = useTranslation()
  const [, theme] = useStyletron()
  const { deleteOffer, duplicateOffer, handleSorting, sort, getSortedData } = useOfferManagement({
    t,
    deleteRoute,
    duplicateRoute,
    providerUuid,
    onDeleteSuccess,
    onDuplicateSuccess,
  })
  const { tableRow, headingDisplay, headCellOverrides } = useDashboardTableStyles()

  const navigate = useNavigate()
  const Table = withStyle(StyledTable, {
    alignContent: 'start',
  })

  return (
    <Table role='grid' $gridTemplateColumns='34% 6% 7% 7% 7% 10% 29%'>
      <div role='row' className={headingDisplay}>
        <SortableHeadCell
          title={t('organizationManagement:name')}
          direction={sort.sortingKey === 'name' ? sort.direction : ''}
          onSort={() => handleSorting('name')}
          overrides={headCellOverrides}
        />
        <SortableHeadCell
          title={t('organizationManagement:table.offer_type')}
          direction={sort.sortingKey === 'energy_type' ? sort.direction : ''}
          onSort={() => handleSorting('energy_type')}
          overrides={headCellOverrides}
        />
        <SortableHeadCell
          title={t('organizationManagement:table.complete')}
          direction={sort.sortingKey === 'complete' ? sort.direction : ''}
          onSort={() => handleSorting('complete')}
          overrides={headCellOverrides}
        />
        <SortableHeadCell
          title={t('organizationManagement:table.comparator_link')}
          direction={sort.sortingKey === 'comparator_link_broken' ? sort.direction : ''}
          onSort={() => handleSorting('comparator_link_broken')}
          overrides={headCellOverrides}
        />
        <SortableHeadCell
          title={t('organizationManagement:table.status')}
          direction={sort.sortingKey?.includes('status') ? sort.direction : ''}
          onSort={() => handleSorting('status')}
          overrides={headCellOverrides}
        />
        <SortableHeadCell
          title={t('organizationManagement:table.inactive_date')}
          direction={sort.sortingKey?.includes('inactiveDate') ? sort.direction : ''}
          onSort={() => handleSorting('inactiveDate')}
          overrides={headCellOverrides}
        />
        <SortableHeadCell title={t('common:actions')} overrides={headCellOverrides} />
      </div>
      {getSortedData(items).map((row, index) => {
        const striped = index % 2 === 0
        return (
          <div role='row' className={tableRow} key={row.id}>
            <StyledBodyCell $striped={striped}>{row.name}</StyledBodyCell>
            <StyledBodyCell $striped={striped}>{row.energy_type}</StyledBodyCell>
            <StyledBodyCell $striped={striped}>
              {row.is_complete ? (
                <IconCarbonCheckmarkOutline color='#197676' size={32} />
              ) : (
                <IconCarbonWarningAlt color='#FF0000' size={32} />
              )}
            </StyledBodyCell>
            <StyledBodyCell $striped={striped}>
              <ComparatorLinkState row={row} t={t} />
            </StyledBodyCell>
            <StyledBodyCell $striped={striped}>
              {row.active ? (
                <IconCarbonCheckmarkOutline color='#197676' size={32} />
              ) : (
                <IconCarbonPauseOutline color='#FF0000' size={32} />
              )}
            </StyledBodyCell>
            <StyledBodyCell $striped={striped}>{row.inactive_date ? row.inactive_date : '-'}</StyledBodyCell>
            <StyledBodyCell $striped={striped}>
              <>
                <SDButton
                  key={`modify-${row.id}`}
                  onClick={() =>
                    navigate('/energie/organisations/provider/' + providerUuid + '/offers/' + row.id + '/edit')
                  }
                  size={SIZE.compact}
                  style={{ marginRight: '1rem' }}
                >
                  {t('common:edit')}
                </SDButton>
                <SDButton
                  key={`duplicate-${row.id}`}
                  onClick={() => duplicateOffer(row.id)}
                  size={SIZE.compact}
                  style={{ marginRight: '1rem', backgroundColor: theme.colors.accent500 }}
                >
                  {t('common:duplicate')}
                </SDButton>
                <SDButton
                  key={`delete-${row.id}`}
                  onClick={() => deleteOffer(row.id)}
                  size={SIZE.compact}
                  style={{ backgroundColor: theme.colors.negative500 }}
                >
                  {t('common:delete')}
                </SDButton>
              </>
            </StyledBodyCell>
          </div>
        )
      })}
    </Table>
  )
}

EnergyOffersTable.propTypes = {
  items: PropTypes.array,
  providerUuid: PropTypes.string,
  deleteRoute: PropTypes.string,
  duplicateRoute: PropTypes.string,
  onDeleteSuccess: PropTypes.func,
  onDuplicateSuccess: PropTypes.func,
}

export default EnergyOffersTable
